import Konva from 'konva';
import axios from 'axios';
import WaveformData from 'waveform-data';

function reportError(e, options) {
  // nothing for now
  // eslint-disable-next-line no-console
  console.error(e, options);
}

const utils = {
  clamp(value, min, max) {
    if (value < min) {
      return min;
    } else if (value > max) {
      return max;
    }

    return value;
  },

  scaleY(amplitude, height, scale) {
    const range = 256;
    const offset = 128;
    const scaledAmplitude = ((amplitude * scale + offset) * height) / range;

    return height - utils.clamp(height - scaledAmplitude, 0, height);
  },
};

const noop = () => {
  // do nothing
};

export default class WaveformProgress {
  constructor(options) {
    this.container = null; // options.container;
    // this.width = options.container.offsetWidth;
    // this.height = options.container.offsetHeight;
    this.playedColor = options.playedColor;
    this.unplayedColor = options.unplayedColor;
    this.fadedPlayedColor = options.fadedPlayedColor;
    this.playedLayer = new Konva.Layer({ listening: false });
    this.unplayedLayer = new Konva.Layer({ listening: false });
    this.seekLayer = new Konva.Layer({ listening: false });
    this.changedUnplayedColor = false;
    this.currPos = 0;
  }

  /* ==== Private methods first ==== */

  drawChannel(
    context,
    shape,
    progress,
    channel,
    frameOffset,
    startPixels,
    endPixels,
    top,
    height
  ) {
    const amplitudeScale = 1; // this._view.getAmplitudeScale();
    const stopPixel = progress
      ? this.timeToPixels(
        shape.attrs.name === 'playedShape' ? this.currPos : this.drawToTime
      )
      : endPixels;

    try {
      context.beginPath();

      for (let x = startPixels; x < stopPixel; x++) {
        const val = channel.min_sample(x);

        context.lineTo(
          x - frameOffset + 0.5,
          Math.min(
            top + utils.scaleY(val, height, amplitudeScale) + 0.5,
            this.container.offsetHeight / 2 + 1.5
          )
        );
      }

      for (let x = stopPixel - 1; x >= startPixels; x--) {
        const val = channel.max_sample(x);

        context.lineTo(
          x - frameOffset + 0.5,
          Math.max(
            top + utils.scaleY(val, height, amplitudeScale) + 0.5,
            this.container.offsetHeight / 2 + 1.5
          )
        );
      }

      context.closePath();
      context.fillShape(shape);
      // context.fillStrokeShape(shape);
    } catch (e) {
      // Do nothing
    }
  }

  drawWaveform(progress, context, shape) {
    const limit = this.frameOffset + this.container.offsetWidth;
    let startPixels = this.startPixels;
    let endPixels = this.endPixels;

    if (startPixels < this.frameOffset) {
      startPixels = this.frameOffset;
    }

    if (endPixels > limit) {
      endPixels = limit;
    }

    if (this.waveformData && endPixels > this.waveformData.length) {
      endPixels = this.waveformData.length;
    }

    const channels = this.waveformData.channels;
    let waveformTop = 0;
    let waveformHeight = Math.floor(this.container.offsetHeight / channels);

    for (let i = 0; i < channels; i++) {
      if (i === channels - 1) {
        waveformHeight =
          this.container.offsetHeight - (channels - 1) * waveformHeight;
      }

      this.drawChannel(
        context,
        shape,
        progress,
        this.waveformData.channel(i),
        this.frameOffset,
        startPixels,
        endPixels,
        waveformTop,
        this.container.offsetHeight
      );

      waveformTop += waveformHeight;
    }
  }

  timeToPixels(position) {
    return Math.floor(
      (position * this.waveformData.sample_rate) / this.waveformData.scale
    );
  }

  /* ==== Public methods ==== */

  destroy() {
    if (this.stage) this.stage.destroy();
  }

  /** Draws the initial waveform */
  draw(container, start, end) {
    this.container = container;
    this.width = container.offsetWidth;
    this.height = container.offsetHeight;

    let scale, width;

    if (this.container.offsetWidth <= 0) {
      // this is trouble, bye!
      // throw new Error(
      //   'Invalid container offsetWidth of ' + this.container.offsetWidth
      // );
      return;
    }

    try {
      if (typeof start !== 'undefined' && typeof end !== 'undefined') {
        scale = Math.floor(
          ((end - start) * this.waveformData.sample_rate) /
            this.container.offsetWidth
        );

        this.waveformData = this.waveformData.resample({
          scale,
        });
        this.frameOffset = this.timeToPixels(start);
      } else {
        width = this.container.offsetWidth;

        this.waveformData = this.waveformData.resample({
          width,
        });
      }
    } catch (e) {
      reportError(
        e,
        {
          while: 'resampling waveform data',
        },
        {
          waveformDataExists: !!this.waveformData,
          scale,
          width,
        }
      );
    }

    this.startPixels = this.frameOffset;
    this.endPixels = this.frameOffset + this.container.offsetWidth;

    let startingWaveColor = this.playedColor;

    if (this.unplayedShape) {
      this.unplayedShape.destroy();
      this.playedShape.destroy();
      this.seekShape.destroy();
      this.stage.draw();
      startingWaveColor = this.unplayedColor;
    }

    this.unplayedShape = new Konva.Shape({
      name: 'unplayedShape',
      fill: startingWaveColor,
      // stroke: startingWaveColor,
      // strokeWidth: 0.5,
      sceneFunc: this.drawWaveform.bind(this, false),
    });
    this.playedShape = new Konva.Shape({
      name: 'playedShape',
      fill: this.playedColor,
    });
    this.seekShape = new Konva.Shape({
      name: 'seekShape',
      fill: this.playedColor,
    });

    this.unplayedLayer.add(this.unplayedShape);
    this.playedLayer.add(this.playedShape);
    this.seekLayer.add(this.seekShape);

    // if (this.stage) {
    //   this.stage.destroy();
    //   this.stage = null;
    // }

    if (!this.stage) {
      this.stage = new Konva.Stage({
        container: this.container,
        width: this.width,
        height: this.height,
      });
      this.stage.add(this.unplayedLayer);
      this.stage.add(this.playedLayer);
      this.stage.add(this.seekLayer);
    }

    this.unplayedShape.draw();
  }

  fadeProgressLayer(fade) {
    if (!this.playedShape || !this.playedLayer) return;

    this.playedShape.fill(fade ? this.fadedPlayedColor : this.playedColor);
    this.drawToTime = this.currPos;
    this.playedLayer.draw();
  }

  async load(url) {
    const response = await axios.get(url, {
      responseType: 'arraybuffer',
      withCredentials: false,
    });

    this.originalWaveformData = WaveformData.create(response.data);
    this.waveformData = WaveformData.create(response.data);
  }

  /** Resize/redraw waveform based on container width */
  resize() {
    if (!this.container || this.container.getBoundingClientRect().width <= 0) {
      return;
    }

    this.width = this.container.getBoundingClientRect().width;
    this.stage && this.stage.width(this.width);
    this.endPixels = this.frameOffset + this.container.offsetWidth;
    this.waveformData = this.originalWaveformData.resample({
      width: this.width,
    });
  }

  /** Sets the current playback position */
  setCurrentPosition(
    /** Position in seconds */
    position
  ) {
    if (!this.stage || !this.playedShape) return; // no point

    if (!this.playedShape.sceneFunc()) {
      this.playedShape.sceneFunc(this.drawWaveform.bind(this, true));
    }

    this.currPos = position;

    if (!this.changedUnplayedColor) {
      this.unplayedShape.fill(this.unplayedColor);
      this.changedUnplayedColor = true;
    }

    this.drawToTime = position;
    this.playedLayer.draw();
  }

  setPotentialSeekPosition(position) {
    if (!this.stage || !this.seekShape) return;

    if (!this.seekShape.sceneFunc() || this.seekShape.sceneFunc() === noop) {
      this.seekShape.sceneFunc(this.drawWaveform.bind(this, true));
    }

    if (position > this.currPos) {
      this.playedLayer.zIndex(2);
      this.seekLayer.zIndex(1);
      this.playedShape.fill(this.playedColor);
      this.seekShape.fill(this.fadedPlayedColor);
    } else {
      this.playedLayer.zIndex(1);
      this.seekLayer.zIndex(2);
      this.playedShape.fill(this.fadedPlayedColor);
      this.seekShape.fill(this.playedColor);
    }

    this.drawToTime = position;
    this.seekLayer.draw();

    if (position === 0) {
      this.seekShape.sceneFunc(noop);
    }
  }
}
